<script setup lang="ts">
import {
  CheckboxField,
  ChoiceField,
  HelpTextTooltip,
  TextField
} from '@haskoning/vue-component-library'
import { useCalculationFormComposable } from '@/components/calculation-form/composable/useCalculationFormComposable'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { defineProps } from 'vue'
import { ENVIRONMENT_LOAD_CASE_FIELDS } from '@/components/calculation-form/constants/constants'

const props = defineProps<CalculationFormMixinProps>()
const emit = defineEmits(['updateTabCompletion'])
const {
  calculationStore,
  currentItem,
  isValid,
  formRef,
  disabled,
  isReadOnly,
  hasHelpText,
  lowerValidator,
  upperValidator
} = useCalculationFormComposable(props, emit)
defineExpose({ formRef })

const environmentLoadCaseChoiceFields = ['calculation_method', 'calculation_type']
</script>

<template>
  <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
    <v-card elevation="0" flat>
      <v-card-title class="header-font d-flex justify-space-between align-center">
        Environment load case
        <div class="d-flex justify-end">
          <slot name="resetInputButton" />
        </div>
      </v-card-title>
      <v-card-subtitle class="description-font">
        Variable site specific parameters
      </v-card-subtitle>

      <v-card-text>
        <v-row
          align="center"
          no-gutters
          v-for="field in environmentLoadCaseChoiceFields"
          :key="field"
        >
          <v-col cols="12" md="8" lg="4" class="d-flex align-center">
            <HelpTextTooltip
              v-if="hasHelpText(field)"
              :text="calculationStore.fields[field].help_text || ''"
            />
            <span :class="hasHelpText(field) ? 'px-2' : 'px-0'">
              {{ calculationStore.fields[field].label }}
            </span>
          </v-col>

          <v-col cols="12" md="8" lg="4" class="pt-0">
            <ChoiceField
              :field="calculationStore.fields[field]"
              :model-value="currentItem"
              :no-help-text="true"
              :readonly="isReadOnly"
              @update="calculationStore.update(currentItem)"
            />
          </v-col>
        </v-row>

        <v-row
          align="start"
          no-gutters
          v-for="field in ENVIRONMENT_LOAD_CASE_FIELDS"
          :key="field.INDIVIDUAL_VALUE"
        >
          <v-col cols="12" md="8" lg="4" class="d-flex align-center pt-7">
            <HelpTextTooltip
              v-if="hasHelpText(field.INDIVIDUAL_VALUE)"
              :text="calculationStore.fields[field.INDIVIDUAL_VALUE].help_text || ''"
            />
            <span :class="hasHelpText(field.INDIVIDUAL_VALUE) ? 'px-2' : 'px-0'">
              {{ calculationStore.fields[field.INDIVIDUAL_VALUE].label }}
            </span>
          </v-col>

          <v-col cols="12" md="8" lg="4" class="pt-0">
            <TextField
              v-if="!currentItem[field.RANGE_INDICATOR]"
              :field="calculationStore.fields[field.INDIVIDUAL_VALUE]"
              :model-value="currentItem"
              :no-help-text="true"
              :readonly="isReadOnly"
              @update="calculationStore.update(currentItem)"
            />

            <template v-else>
              <v-row align="center" no-gutters class="ga-4">
                <v-col v-for="(key, index) in field.RANGE_VALUES" :key="key">
                  <TextField
                    :field="calculationStore.fields[key]"
                    :model-value="currentItem"
                    :label="['min', 'max', 'step'][index]"
                    :no-help-text="true"
                    :readonly="isReadOnly"
                    :custom-error-rules="[
                      {
                        0: (value: number) => lowerValidator(value, key),
                        1: (value: number) => upperValidator(value, key)
                      }[index] || ((value: number) => true)
                    ]"
                    @update="calculationStore.update(currentItem)"
                  />
                </v-col>
              </v-row>
            </template>

            <CheckboxField
              :field="calculationStore.fields[field.RANGE_INDICATOR]"
              :model-value="currentItem"
              :no-help-text="true"
              :readonly="isReadOnly"
              @update="calculationStore.update(currentItem)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<style scoped>
.v-input :deep(.v-input__control) {
  margin-left: -10px;
}

.v-input :deep(.v-input__details) {
  display: none;
}
</style>
